import { Injectable } from '@angular/core';
import { filter, find, includes, reduce, reverse, sortBy, startCase } from 'lodash';
import { IDropdownOption } from 'Src/ng2/shared/typings/interfaces/design-library.interface';
import { IStudentAssessment } from '../../../../shared/typings/interfaces/student-assessment.interface';
import { IAssessmentInputRow } from './assessments-data-input.component';

export interface IAssmntGroupingAll {
  [groupBy: string]: {
    groupedAssessments: {
      [name: string]: {
        rowData: IAssessmentInputRow[];
      };
    };
    options: IDropdownOption[];
  };
}

@Injectable()
export class AssessmentsDataInputService {
  constructor() {
    //
  }

  sortGroupings({
    rowData,
    sort,
  }: {
    rowData: IAssessmentInputRow[];
    sort: { key: string; dir: string };
  }): IAssessmentInputRow[] {
    if (sort.dir === 'asc') {
      return sortBy(rowData, sort.key);
    } else {
      return reverse(sortBy(rowData, sort.key));
    }
  }

  getAllGrouping(studentAssessments: IStudentAssessment[]): IAssmntGroupingAll {
    const output = {
      Grade: {
        groupedAssessments: {},
        options: [],
      },
      Class: {
        groupedAssessments: {},
        options: [],
      },
    };
    return reduce(
      studentAssessments,
      (res, studentAssessment) => {
        const {
          student: { firstName, lastName, studentId, grade, officialClass },
          items,
          assessedOn,
          lastEditedBy,
        } = studentAssessment;
        const lastEdited =
          lastEditedBy && lastEditedBy.firstName ? `${lastEditedBy.firstName} ${lastEditedBy.lastName}` : '—';
        const studentRow: IAssessmentInputRow = {
          studentAssessmentId: studentAssessment._id,
          studentId,
          name: `${lastName}, ${firstName}`,
          instructionalScore: items[1].value,
          independentScore: items[0].value,
          assessmentDate: assessedOn,
          lastEdited,
        };
        const parsedGrade = this._parseGrade(grade);
        const nameByGrade = `Grade ${parsedGrade}`;
        const nameByClass = `${officialClass}`;
        ['Grade', 'Class'].forEach(groupBy => {
          let name;
          let key;
          if (groupBy === 'Grade') {
            name = nameByGrade;
            key = grade;
          } else {
            name = nameByClass;
            key = officialClass;
          }
          if (!res[groupBy].groupedAssessments[name]) {
            res[groupBy].groupedAssessments[name] = { rowData: [] };
            res[groupBy].options = [...res[groupBy].options, { key, human: name }];
          }
          res[groupBy].groupedAssessments[name].rowData = [
            ...res[groupBy].groupedAssessments[name].rowData,
            studentRow,
          ];
        });
        return res;
      },
      output,
    );
  }

  // remove leading 0s from single-digit grades
  _parseGrade(grade: string): string {
    let parsedGrade;
    if (includes(['PK', '10', '11', '12'], grade)) parsedGrade = grade;
    else parsedGrade = grade.substring(1);
    return parsedGrade;
  }

  filterGroupings({ filterTerm, rowData }): IAssessmentInputRow[] {
    const queries = filterTerm.trim()
      ? filterTerm
          .split(/\s+|,/)
          .filter(str => str)
          .map(str => str.toLowerCase())
      : [];
    if (queries.length) {
      rowData = filter(rowData, row => {
        const { name, studentId } = row;
        const targetStr = `${name} ${studentId}`.toLowerCase();
        return !!find(queries, query => {
          return targetStr.includes(query);
        });
      });
    }
    return rowData;
  }

  reshapeGroupingOptions({ gradeOptions, classOptions }): IDropdownOption[] {
    return [
      {
        key: 'GRADE',
        human: 'Grade',
        options: sortBy(gradeOptions, 'human'),
      },
      {
        key: 'CLASS',
        human: 'Class',
        options: sortBy(classOptions, 'human'),
      },
    ];
  }

  // Formats user entered value prior to save.
  // NOTE: For 'Pre-A`, handles variants where user entry may be one of 'Pre-A', 'PRE-A', 'pre-A', or 'Pre-a'
  scoreCasing(score) {
    let formattedScore;
    score = score.toLowerCase();
    if (score.length === 1) {
      formattedScore = score.toUpperCase();
    } else {
      formattedScore = startCase(score).replace(' ', '-');
    }
    return score === '' ? null : formattedScore;
  }
}
